import { Group } from "./group/group.model";
import { Team } from "../team/team.model";
import { CrudAbstractModel } from "../crud-abstract/crud-abstract.model";
import { Organization } from "../organization/organization.model";
import { EmailIntegrationType } from "./emailIntegrationType.const";
import { Company } from "app/company/company.model";
import { role } from "./role.const";

export interface UserStats {
  activeLastMonth: number;
  activeThisMonth: number;
  totalActive: number;
}

export class User extends CrudAbstractModel {
  constructor(
    public id: number,
    private username?: string,
    private email?: string,
    private password?: string,
    private lastLogin?: string,
    private activeAt?: string,
    private roles?: string[],
    private groups?: Group[],
    private firstName?: string,
    private lastName?: string,
    private team?: Team,
    private company?: Company,
    private phone?: string,
    private ref?: string,
    private signature?: string,
    private enabled?: boolean,
    private limitedCrm?: boolean,
    private language?: string,
    private mailIntegrationEnabled?: boolean,
    private mailUsername?: string,
    private mailPassword?: string,
    private mailImapPath?: string,
    private mailMsGraphFolder?: string,
    private emailIntegrationType?: EmailIntegrationType,
    private lastQuoteType?: string,
    private modules?: string[],
    private companiesAvailable?: boolean,
    private emailIntegrationConnected?: boolean,
  ) {
    super(id);
  }

  public getUsername(): string {
    return this.username;
  }

  public getPassword(): string {
    return this.password;
  }

  public getRoles(): string[] {
    return this.roles;
  }

  public setRoles(roles: string[]): void {
    this.roles = roles;
  }

  public getGroups(): Group[] {
    return this.groups;
  }

  public hasRole(role: string): boolean {
    return -1 !== this.roles.indexOf(role);
  }

  public getEmail(): string {
    return this.email;
  }

  public geLastLogin(): string {
    return this.lastLogin;
  }

  public getActiveAt(): string {
    return this.activeAt;
  }

  public getFirstName(): string {
    return this.firstName;
  }

  public getLastName(): string {
    return this.lastName;
  }

  public getFullName(): string {
    return this.firstName && this.lastName
      ? this.firstName + " " + this.lastName
      : this.username;
  }

  public get fullName(): string {
    return this.firstName && this.lastName
      ? this.firstName + " " + this.lastName
      : this.username;
  }

  public getTeam(): Team {
    return this.team;
  }

  public getCompany(): Company {
    return this.company;
  }

  public setCompany(company: Company): void {
    this.company = company;
  }

  public get organization(): Organization {
    return this.getTeam() ? this.getTeam().getOrganization() : null;
  }

  public get companyName(): string {
    return this.company ? this.company.name : "";
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public isLimitedCrm(): boolean {
    return this.limitedCrm;
  }

  public hasCompanies(): boolean {
    return !!this.companiesAvailable;
  }

  // Mirrors the backend CompanyVoter rule for listing companies.
  public canListCompanies(): boolean {
    return !!this.roles?.includes(role.superAdmin) || !this.company?.getId();
  }

  public getLanguage(): string {
    return this.language;
  }

  public setLanguage(language: string): void {
    this.language = language;
  }

  public getModules(): string[] | null {
    return this.modules;
  }

  public getOrganizationLogoUrl(): string {
    if (
      this.team &&
      !!this.team.getOrganization() &&
      !!this.team.getOrganization().getLogo()
    ) {
      return this.team.getOrganization().getLogo().getPath();
    }
  }
  public setOrganizationLogoUrl(param: string): void {
    if (
      this.team &&
      !!this.team.getOrganization() &&
      !!this.team.getOrganization().getLogo()
    ) {
      this.team.getOrganization().getLogo().setPath(param);
    }
  }

  public isMailIntegrationEnabled(): boolean {
    return this.mailIntegrationEnabled;
  }

  public getMailImapPath(): string {
    return this.mailImapPath;
  }

  public getMailMsGraphFolder(): string {
    return this.mailMsGraphFolder;
  }

  public getMailUserName(): string {
    return this.mailUsername;
  }

  public getSignature(): string {
    return this.signature;
  }

  public getPhone(): string {
    return this.phone;
  }

  public getRef(): string {
    return this.ref;
  }

  public getEmailIntegrationType(): string {
    return this.emailIntegrationType;
  }

  public isEmailIntegrationConnected(): boolean {
    return !!this.emailIntegrationConnected;
  }

  public getLastQuoteType(): string {
    return this.lastQuoteType;
  }

  public setLastQuoteType(lastQuoteType: string): void {
    if (lastQuoteType !== "email") {
      this.lastQuoteType = lastQuoteType;
    }
  }

  public getRequestParams() {
    const params = {
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      team: this.team.getId(),
      company: this.company ? this.company?.getId() : null,
      phone: this.phone,
      signature: this.signature,
      enabled: this.enabled,
      limitedCrm: this.limitedCrm,
      language: this.language,
      groups: this.groups.map((group) => group.getId()),
      username: this.getUsername(),
      mailIntegrationEnabled: this.isMailIntegrationEnabled(),
      mailImapPath: this.getMailImapPath(),
      mailMsGraphFolder: this.getMailMsGraphFolder(),
      mailUsername: this.getMailUserName(),
      emailIntegrationType: this.getEmailIntegrationType(),
    };

    if (this.password) {
      params["plainPassword"] = this.password;
    }

    if (this.mailPassword) {
      params["mailPassword"] = this.mailPassword;
    }

    return params;
  }
}
